import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useApiQuery } from '../hooks/useApiQuery';
import { User } from '../utils/roleUtils';
import {
  extractUser,
  AUTH_STORAGE_EVENT,
  getAuthQueryKey,
  getCurrentUserRequest,
  getStoredRole,
  getStoredToken,
  isPartnerRole,
  setStoredRole,
} from '../utils/auth';

interface AuthContextType {
  user: User | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isPartner: boolean;
  isCanvasser: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [, setAuthRevision] = useState(0);
  const storedRole = getStoredRole();
  const hasToken = !!getStoredToken();
  const { data, isLoading } = useApiQuery<unknown>({
    queryKey: getAuthQueryKey(storedRole),
    request: getCurrentUserRequest(storedRole),
    enabled: hasToken,
    retry: false,
  });
  const user = hasToken ? extractUser(data, storedRole) : undefined;

  useEffect(() => {
    if (user?.role) {
      setStoredRole(user.role);
    }
  }, [user?.role]);

  useEffect(() => {
    const refreshAuthState = () => setAuthRevision((revision) => revision + 1);

    window.addEventListener(AUTH_STORAGE_EVENT, refreshAuthState);
    window.addEventListener('storage', refreshAuthState);

    return () => {
      window.removeEventListener(AUTH_STORAGE_EVENT, refreshAuthState);
      window.removeEventListener('storage', refreshAuthState);
    };
  }, []);

  const value: AuthContextType = {
    user,
    isLoading: hasToken ? isLoading : false,
    isAuthenticated: hasToken && !!user,
    isAdmin: user?.role === 'SYSTEM_ADMIN',
    isPartner: isPartnerRole(user?.role),
    isCanvasser: user?.role === 'CANVASSER',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
